import Component from '@glimmer/component'
import { isEmpty } from '@ember/utils'
import { service } from '@ember/service'
import { formatValue } from 'teacher-ui-client/utils/format-value'
import { themeKeys as statBoxThemes } from 'teacher-ui-client/components/giraffe/stat-box/themes'
import type { IntlService } from 'ember-intl'
import { StatBox } from 'teacher-ui-client/components/giraffe/stat-box'
import type { PeaksProgressData } from 'teacher-ui-client/utils/student-events/fetchers/peaks-progress'
import type { UIScopeName } from 'teacher-ui-client/reporting/ui-scope/route'
import type { PeaksProgressDetailsData } from 'teacher-ui-client/utils/student-events/fetchers/peaks-progress-details'

type SummaryDataCategories = keyof PeaksProgressData['summary']

interface Args {
  scope: UIScopeName | 'student'
  data: PeaksProgressData['summary'] | PeaksProgressDetailsData['summary']
}

interface Signature {
  Element: HTMLElement
  Args: Args
}

export class PeaksProgressComponent extends Component<Signature> {
  @service intl!: IntlService

  get dataScope() {
    return this.args.scope
  }

  get categories(): { category: SummaryDataCategories; theme: (typeof statBoxThemes)[keyof typeof statBoxThemes] }[] {
    return [
      {
        category: 'lessons',
        theme: statBoxThemes.THEME_OCEANY_BLUE,
      },
      {
        category: 'lesson_quizzes',
        theme: statBoxThemes.THEME_JUICY_ORANGE,
      },
      {
        category: 'sound_gems',
        theme: statBoxThemes.THEME_PURPLY_PINK,
      },
      {
        category: 'word_gems',
        theme: statBoxThemes.THEME_WL_BLUE,
      },
      {
        category: 'book_quizzes',
        theme: statBoxThemes.THEME_GRAPEY_GREEN,
      },
    ]
  }

  getSummaryDataForCategory(
    summary: PeaksProgressData['summary'] | PeaksProgressDetailsData['summary'],
    category: SummaryDataCategories,
  ) {
    let mainStatValue
    switch (category) {
      case 'lessons':
        mainStatValue = summary.lessons.lessons_completed
        break
      case 'lesson_quizzes':
        mainStatValue = summary.lesson_quizzes.student_average
        break
      case 'sound_gems':
        mainStatValue = summary.sound_gems.sound_gems_earned
        break
      case 'word_gems':
        mainStatValue = summary.word_gems.word_gems_earned
        break
      case 'book_quizzes':
        mainStatValue = summary.book_quizzes.books_read
        break
      default:
    }

    const perStudentAverageValue = 'student_average' in summary[category] ? summary[category].student_average : null

    return { mainStatValue, perStudentAverageValue }
  }

  get statBoxes() {
    const summary = this.args.data
    if (isEmpty(summary)) return []

    const prefix = 'reporting.fastPhonics.peaksProgress.statBoxes'
    const { categories, dataScope, intl } = this

    const boxes = categories.map((categoryObj) => {
      const { category } = categoryObj
      const { theme } = categoryObj
      const title = intl.t(`${prefix}.titles.${category}`)

      const categorySummaryData = this.getSummaryDataForCategory(summary, category)

      let stats

      if (category === 'lesson_quizzes') {
        // lesson quizzes only have a single stat, the average quiz score
        const mainStat = {
          name: intl.t(`${prefix}.statCategories.average`),
          value: formatValue(categorySummaryData.mainStatValue, { append: '%' }),
        }
        stats = [[mainStat]]
      } else {
        let name = intl.t(`${prefix}.statCategories.total.student`)
        if (intl.exists(`${prefix}.statCategories.total.${dataScope}`)) {
          name = intl.t(`${prefix}.statCategories.total.${dataScope}`)
        }
        const mainStat = {
          name,
          value: formatValue(categorySummaryData.mainStatValue),
        }
        const perStudentStat = {
          name: intl.t(`${prefix}.statCategories.perStudentAverage`),
          value: formatValue(categorySummaryData.perStudentAverageValue),
        }
        // No need for the per student stat if only looking at a single student
        stats = dataScope === 'student' ? [[mainStat]] : [[mainStat, perStudentStat]]
      }

      return { category, title, theme, stats }
    })

    return boxes
  }
  <template>
    <div class="flex flex-wrap justify-center" ...attributes>
      {{#each this.statBoxes as |statBox|}}
        <StatBox
          data-test-stat-box={{statBox.category}}
          class="managed-size w-[325px] max-w-[325px] min-w-[210px] shrink grow basis-0 border-r border-b border-white print:mb-[10px] [&>.stat_value]:text-[26px] [&>.stats]:text-[26px]"
          @title={{statBox.title}}
          @theme={{statBox.theme}}
          @stats={{statBox.stats}}
        />
      {{/each}}
    </div>
  </template>
}

export default PeaksProgressComponent
